// ═══════════════════════════════════════════════════════════
// INDUSTRY INTELLIGENCE — Contextual content for 9 industries
// Surfaces dynamically in wizard steps, results, and landing page
// ═══════════════════════════════════════════════════════════

export const INDUSTRY_INTELLIGENCE = {

  // ─── BFSI ───────────────────────────────────────────────
  bfsi: {
    label: 'Banking, Financial Services & Insurance',
    shortLabel: 'BFSI',
    commonProcesses: [
      'KYC & customer onboarding (Aadhaar eKYC, Video KYC)',
      'Loan origination, credit scoring & disbursement',
      'Insurance underwriting & claims settlement',
      'UPI / NEFT / RTGS payment reconciliation',
      'Anti-money laundering (AML) screening',
      'Portfolio management & financial advisory',
    ],
    typicalDataElements: [
      { element: 'Aadhaar, PAN, passport, voter ID numbers', category: 'Government IDs' },
      { element: 'Bank accounts, credit cards, UPI IDs', category: 'Financial' },
      { element: 'CIBIL/credit scores & repayment history', category: 'Financial' },
      { element: 'Income proofs, salary slips, ITR copies', category: 'Financial' },
      { element: 'Nominee & beneficiary details', category: 'Identity' },
    ],
    missedDataPoints: [
      { point: 'CIBIL score pulls & credit bureau inquiry logs', risk: 'high', note: 'Every credit check is a data processing event under DPDP' },
      { point: 'Voice recordings of customer service & collections calls', risk: 'high', note: 'Call recordings are personal data with retention obligations' },
      { point: 'IP addresses & device data from net banking / mobile app', risk: 'medium', note: 'Digital identifiers are personal data under DPDP' },
      { point: 'WhatsApp & SMS logs with customer account details', risk: 'medium', note: 'Business messages containing personal info need consent basis' },
      { point: 'CCTV footage in branches and ATM camera recordings', risk: 'medium', note: 'Visual data of individuals requires notice and retention limits' },
    ],
    typicalSystems: ['Core Banking (Finacle, Flexcube)', 'CRM (Salesforce, LeadSquared)', 'Loan Origination System', 'Insurance TPA platforms', 'Payment gateways (Razorpay, BillDesk)', 'Excel/Sheets for MIS reporting'],
    dpdpNotes: [
      'RBI-regulated entities will likely face early DPDP enforcement, building on existing data governance circulars.',
      'KYC data has dual regulation — RBI mandates collection, but DPDP requires purpose limitation and deletion timelines.',
      'Insurance claims often involve health data — classified as sensitive, requiring enhanced safeguards.',
    ],
    stepHints: {
      activities: 'In BFSI, KYC alone generates 15+ data points per customer. Loan processing adds credit history, income proof, and employer details.',
      subjects: 'Think beyond customers — loan guarantors, insurance nominees, joint account holders, and authorised signatories are all data principals under DPDP.',
      elements: 'BFSI handles the widest range of sensitive identifiers — Aadhaar for eKYC, PAN for tax, bank details for transactions, and CIBIL scores for lending.',
      systems: 'Legacy core banking systems, Excel-based MIS reports, and archived loan files in shared drives are where the most untracked personal data hides.',
    },
    blindSpotBanner: 'BFSI firms routinely overlook call recordings, CIBIL data pulls, and WhatsApp communications as regulated data processing.',
    useCaseCard: {
      headline: 'Is your KYC data DPDP-ready?',
      stat: '15+ data points per customer',
      description: 'Banks and insurers handle the densest personal data per individual. Most miss half of what\'s regulated.',
    },
    relevance: {
      activities: ['customer_kyc', 'payments', 'vendor_mgmt'],
      subjects: ['customers', 'employees', 'vendors'],
      elements: ['contact_info', 'gov_ids', 'financial'],
      systems: ['crm', 'cloud', 'onprem'],
    },
  },

  // ─── HEALTHCARE ─────────────────────────────────────────
  healthcare: {
    label: 'Healthcare & Life Sciences',
    shortLabel: 'Healthcare',
    commonProcesses: [
      'Patient registration & OPD/IPD management',
      'Electronic medical records (EMR/EHR) management',
      'Lab reports & diagnostic imaging (PACS)',
      'Pharmacy dispensing & prescription tracking',
      'Insurance TPA claims & cashless processing',
      'Telemedicine consultations & remote monitoring',
    ],
    typicalDataElements: [
      { element: 'Patient names, DOB, blood group, emergency contacts', category: 'Identity' },
      { element: 'Health records — diagnosis, prescriptions, surgery notes', category: 'Sensitive' },
      { element: 'Aadhaar/Ayushman Bharat Health ID', category: 'Government IDs' },
      { element: 'Insurance policy numbers & claim history', category: 'Financial' },
      { element: 'Family medical history & genetic predisposition notes', category: 'Sensitive' },
    ],
    missedDataPoints: [
      { point: 'Lab reports shared via email or WhatsApp to patients', risk: 'high', note: 'Health data sent over unencrypted channels still counts as processing' },
      { point: 'Patient photographs taken for clinical documentation', risk: 'high', note: 'Medical images are sensitive personal data with strict handling rules' },
      { point: 'Doctor referral notes containing patient history', risk: 'medium', note: 'Data shared between providers needs purpose limitation' },
      { point: 'Visitor logs and CCTV in hospital reception areas', risk: 'medium', note: 'Visual data of visitors requires notice at point of collection' },
      { point: 'Wearable/IoT device data from remote patient monitoring', risk: 'medium', note: 'Continuous health monitoring generates high volumes of sensitive data' },
    ],
    typicalSystems: ['Hospital Management System (HMS/HIS)', 'Lab Information System (LIS)', 'PACS / radiology systems', 'Pharmacy management software', 'WhatsApp (doctor-patient communication)', 'Excel/paper registers in smaller clinics'],
    dpdpNotes: [
      'Health data is among the most sensitive categories under DPDP — expect enhanced safeguards and possible SDF classification.',
      'Children\'s vaccination and paediatric records require verifiable parental consent under DPDP.',
      'Telemedicine creates cross-state data flows that need careful data residency planning.',
    ],
    stepHints: {
      activities: 'Patient registration, insurance claims, and even appointment reminders all involve personal data processing under DPDP.',
      subjects: 'Patients are obvious, but don\'t forget their family members listed as emergency contacts, insurance nominees, and accompanying minors.',
      elements: 'Health records, prescriptions, and diagnostic images are classified as sensitive data — the highest protection category under DPDP.',
      systems: 'WhatsApp is the most common untracked system in Indian healthcare. Lab reports, prescriptions, and appointment reminders often flow through it.',
    },
    blindSpotBanner: 'Hospitals routinely share lab reports via WhatsApp and email — each instance is a personal data processing event under DPDP.',
    useCaseCard: {
      headline: 'Is patient data leaving through WhatsApp?',
      stat: 'Most sensitive data category',
      description: 'Health records require the highest safeguards. Most clinics share them over uncontrolled channels daily.',
    },
    relevance: {
      activities: ['customer_kyc', 'employee_payroll'],
      subjects: ['customers', 'employees', 'visitors'],
      elements: ['contact_info', 'gov_ids', 'health'],
      systems: ['onprem', 'saas', 'sheets'],
    },
  },

  // ─── IT / SaaS / TECHNOLOGY ─────────────────────────────
  it_saas: {
    label: 'IT / SaaS / Technology',
    shortLabel: 'IT/SaaS',
    commonProcesses: [
      'Customer onboarding & user account creation',
      'Product analytics & usage tracking',
      'Support ticket management & live chat',
      'Employee hiring, onboarding & offboarding',
      'Marketing automation & email campaigns',
      'Billing, subscription management & renewals',
    ],
    typicalDataElements: [
      { element: 'User emails, names, profile photos', category: 'Identity' },
      { element: 'Product usage data, session logs, feature clicks', category: 'Technical' },
      { element: 'Support tickets with customer business context', category: 'Behavioral' },
      { element: 'Employee data across HRMS, Slack, and code repos', category: 'Employment' },
      { element: 'Payment & billing data (Stripe/Razorpay tokens)', category: 'Financial' },
    ],
    missedDataPoints: [
      { point: 'Product telemetry — user-level behaviour data in analytics', risk: 'high', note: 'Mixpanel/Amplitude user tracking is personal data processing' },
      { point: 'Error logs and crash reports containing user context', risk: 'medium', note: 'Sentry/Bugsnag logs often include email, IP, and session data' },
      { point: 'Slack/Teams messages containing customer PII', risk: 'medium', note: 'Internal tools where teams discuss customer issues hold personal data' },
      { point: 'Job applicant data in ATS systems after rejection', risk: 'medium', note: 'Retained resumes need purpose limitation and deletion schedules' },
      { point: 'Third-party cookies and tracking pixels on marketing sites', risk: 'medium', note: 'Cookie consent under DPDP applies to Indian visitors' },
    ],
    typicalSystems: ['Cloud (AWS/GCP/Azure)', 'CRM (HubSpot, Salesforce)', 'Analytics (Mixpanel, GA4, Amplitude)', 'HRMS (Darwinbox, Keka)', 'Communication (Slack, Teams)', 'Code repos & CI/CD (GitHub, GitLab)'],
    dpdpNotes: [
      'SaaS companies are often data processors for their customers AND data fiduciaries for their own users — dual DPDP obligations.',
      'Cross-border data transfers are standard in tech — DPDP requires explicit compliance with data localization rules.',
      'Product telemetry and analytics data often qualify as personal data even when pseudonymized.',
    ],
    stepHints: {
      activities: 'Every SaaS product interaction — sign-ups, feature usage, support tickets — creates a data processing chain from frontend to analytics to cloud storage.',
      subjects: 'Your users are data principals, but so are their end-users if your product processes their data (think: data processor obligations).',
      elements: 'Beyond obvious PII, consider: session recordings, heatmaps, error logs, and analytics events — all tied to identifiable users.',
      systems: 'Your data isn\'t just in your product DB. Check analytics platforms, error trackers, CRM, marketing tools, and internal Slack channels.',
    },
    blindSpotBanner: 'SaaS companies routinely process user data across 10+ tools. Analytics, error tracking, and Slack are the most overlooked sources.',
    useCaseCard: {
      headline: 'Is your product telemetry DPDP-compliant?',
      stat: '10+ tools processing user data',
      description: 'SaaS firms are both data fiduciaries and processors. Most don\'t map where user data actually flows.',
    },
    relevance: {
      activities: ['customer_kyc', 'marketing', 'employee_payroll'],
      subjects: ['customers', 'employees', 'applicants', 'visitors'],
      elements: ['contact_info', 'digital', 'financial'],
      systems: ['cloud', 'saas', 'crm'],
    },
  },

  // ─── RETAIL & E-COMMERCE ────────────────────────────────
  retail: {
    label: 'Retail & E-commerce',
    shortLabel: 'Retail',
    commonProcesses: [
      'Customer registration & account creation',
      'Order processing, payment & delivery',
      'Loyalty programs & reward point management',
      'Marketing — email, SMS, push notifications, retargeting',
      'Returns, refunds & customer support',
      'Store analytics & footfall tracking',
    ],
    typicalDataElements: [
      { element: 'Customer names, phone numbers, delivery addresses', category: 'Identity' },
      { element: 'Payment data — UPI, cards, wallets, COD details', category: 'Financial' },
      { element: 'Purchase history, wish lists, cart data', category: 'Behavioral' },
      { element: 'Device IDs, location data, browsing behaviour', category: 'Technical' },
      { element: 'Loyalty card numbers & reward redemption history', category: 'Behavioral' },
    ],
    missedDataPoints: [
      { point: 'Delivery partner data sharing — customer phone & address', risk: 'high', note: 'Every delivery creates a third-party data transfer under DPDP' },
      { point: 'Abandoned cart data with email & browsing context', risk: 'medium', note: 'Retargeting abandoned carts processes personal data without purchase' },
      { point: 'Store CCTV footage linked to billing timestamps', risk: 'medium', note: 'CCTV combined with purchase data creates identifiable profiles' },
      { point: 'SMS/WhatsApp marketing sent via aggregator platforms', risk: 'medium', note: 'Marketing aggregators are data processors handling your customer lists' },
      { point: 'Return addresses and refund bank details', risk: 'medium', note: 'Refund processing creates separate data records needing retention limits' },
    ],
    typicalSystems: ['E-commerce platform (Shopify, WooCommerce, custom)', 'CRM & marketing automation (Clevertap, MoEngage)', 'POS systems (in-store)', 'Payment gateways (Razorpay, PhonePe)', 'Logistics partners (Delhivery, Shiprocket)', 'Google Analytics / Meta Pixel'],
    dpdpNotes: [
      'Consent for marketing communications is a primary DPDP obligation — opt-in required, not just opt-out.',
      'Delivery involves sharing customer data with logistics partners — each is a data processor relationship.',
      'Loyalty programs store behavioural data over years — DPDP mandates purpose limitation and eventual deletion.',
    ],
    stepHints: {
      activities: 'Every order, marketing campaign, and loyalty interaction creates a data processing chain — from your platform to payment gateways to delivery partners.',
      subjects: 'Your customers are obvious, but website visitors (cookies), delivery recipients, and even store walk-ins (CCTV) are data principals too.',
      elements: 'Purchase history and browsing behaviour are personal data under DPDP. Combined with contact info, they create rich profiles with compliance obligations.',
      systems: 'Don\'t forget logistics partner platforms, marketing tools (Clevertap, MoEngage), and SMS aggregators — they all store your customers\' data.',
    },
    blindSpotBanner: 'Every delivery shares customer phone and address with logistics partners — that\'s a third-party data transfer requiring DPDP compliance.',
    useCaseCard: {
      headline: 'Where does delivery data end up?',
      stat: '3+ third parties per order',
      description: 'Each order shares customer data with payment gateways, logistics, and marketing platforms. That\'s DPDP exposure.',
    },
    relevance: {
      activities: ['marketing', 'payments', 'loyalty'],
      subjects: ['customers', 'visitors'],
      elements: ['contact_info', 'financial', 'digital'],
      systems: ['crm', 'saas', 'cloud'],
    },
  },

  // ─── EDUCATION ──────────────────────────────────────────
  education: {
    label: 'Education & EdTech',
    shortLabel: 'Education',
    commonProcesses: [
      'Student admissions & enrollment',
      'Academic records & examination management',
      'Fee collection & scholarship processing',
      'Learning management (LMS) & online assessments',
      'Alumni tracking & placement services',
      'Parent communication & consent management',
    ],
    typicalDataElements: [
      { element: 'Student names, DOB, parent details, photographs', category: 'Identity' },
      { element: 'Academic records, marksheets, attendance data', category: 'Identity' },
      { element: 'Fee payment details, scholarship bank accounts', category: 'Financial' },
      { element: 'Aadhaar (for scholarships), birth certificates', category: 'Government IDs' },
      { element: 'Learning behaviour — quiz scores, watch time, engagement', category: 'Behavioral' },
    ],
    missedDataPoints: [
      { point: 'Children\'s data processed without verifiable parental consent', risk: 'high', note: 'DPDP requires verifiable parental consent for all under-18 data' },
      { point: 'Alumni data retained indefinitely for placement tracking', risk: 'high', note: 'Graduate data needs defined retention periods and deletion schedules' },
      { point: 'Biometric attendance systems for students and staff', risk: 'medium', note: 'Biometric data of minors requires the highest level of safeguards' },
      { point: 'Parent WhatsApp groups with student information', risk: 'medium', note: 'Sharing student details in groups is broadcasting personal data' },
      { point: 'CCTV in classrooms, corridors, and campus areas', risk: 'medium', note: 'CCTV of minors is among the most sensitive data under DPDP' },
    ],
    typicalSystems: ['ERP (SAP, Oracle, campus management)', 'LMS (Moodle, Google Classroom, custom)', 'Admission portals', 'Fee collection (PayU, Razorpay)', 'WhatsApp groups & parent communication apps', 'Google Workspace / Microsoft 365'],
    dpdpNotes: [
      'Children\'s data (under 18) triggers the strictest DPDP requirements — verifiable parental consent is mandatory.',
      'EdTech platforms tracking learning behaviour must treat engagement data as personal data of minors.',
      'Placement services sharing student resumes with companies is a third-party data transfer under DPDP.',
    ],
    stepHints: {
      activities: 'Admissions, fee collection, and even parent communications all involve processing personal data of minors — the strictest DPDP category.',
      subjects: 'Students under 18 are the key trigger. But parents, alumni, and even visiting speakers whose data you collect are data principals too.',
      elements: 'Academic records and attendance data are personal data. If you use biometric attendance for students, that\'s the highest risk tier.',
      systems: 'WhatsApp parent groups, Google Classroom data, and admission portal databases are frequently overlooked in education data mapping.',
    },
    blindSpotBanner: 'Schools processing children\'s data face the strictest DPDP rules — verifiable parental consent is mandatory, not optional.',
    useCaseCard: {
      headline: 'Is your student data consent-compliant?',
      stat: 'Strictest DPDP category',
      description: 'Children\'s data requires verifiable parental consent. Most institutions don\'t have this in place yet.',
    },
    relevance: {
      activities: ['customer_kyc', 'employee_payroll'],
      subjects: ['customers', 'employees', 'children'],
      elements: ['contact_info', 'gov_ids'],
      systems: ['saas', 'sheets', 'crm'],
    },
  },

  // ─── MANUFACTURING ──────────────────────────────────────
  manufacturing: {
    label: 'Manufacturing & Industrial',
    shortLabel: 'Manufacturing',
    commonProcesses: [
      'Workforce management & shift scheduling',
      'Contractor onboarding & gate pass issuance',
      'Payroll processing — PF, ESI, gratuity',
      'Safety compliance & incident reporting',
      'Visitor management at plant/factory gates',
      'Vendor registration & GST verification',
    ],
    typicalDataElements: [
      { element: 'Employee IDs, Aadhaar, PAN, bank accounts', category: 'Government IDs' },
      { element: 'Contractor details — ID proofs, labour licences', category: 'Identity' },
      { element: 'Biometric attendance — fingerprint, face recognition', category: 'Biometric' },
      { element: 'Medical fitness certificates, injury records', category: 'Sensitive' },
      { element: 'CCTV footage across factory floor and entry gates', category: 'Image' },
    ],
    missedDataPoints: [
      { point: 'Biometric attendance data stored without explicit consent', risk: 'high', note: 'Biometric data requires explicit, informed consent under DPDP' },
      { point: 'Contractor PF/ESI details retained after contract ends', risk: 'high', note: 'Contractor data needs defined retention and deletion after engagement' },
      { point: 'CCTV footage retained beyond reasonable periods', risk: 'medium', note: 'Factory CCTV capturing identifiable individuals is regulated personal data' },
      { point: 'Canteen card or meal swipe data linked to employees', risk: 'low', note: 'Even canteen usage data linked to employee IDs is personal data' },
      { point: 'Medical fitness records of factory workers', risk: 'medium', note: 'Health records of workers are sensitive data under DPDP' },
    ],
    typicalSystems: ['HRMS / payroll (SAP HCM, Keka, GreytHR)', 'Biometric devices (ZKTeco, eSSL)', 'CCTV / DVR systems', 'ERP (SAP, Oracle, Tally)', 'Gate pass & visitor management', 'Excel/Sheets for contractor tracking'],
    dpdpNotes: [
      'Biometric attendance is one of the highest-risk processing activities — explicit consent and strict purpose limitation apply.',
      'Contractor data has the same DPDP obligations as employee data — many manufacturers overlook this.',
      'CCTV in factories captures both employees and visitors — notice requirements and retention limits apply.',
    ],
    stepHints: {
      activities: 'Beyond payroll, think about gate pass systems, safety incident forms, medical checkups, and canteen logs — all involve personal data.',
      subjects: 'Contractors and daily-wage workers are often forgotten data principals. Their Aadhaar, bank details, and biometrics are fully regulated.',
      elements: 'If your factory uses biometric attendance (fingerprint/face), that\'s among the highest-risk data types under DPDP.',
      systems: 'Biometric devices, CCTV DVRs, and Excel-based contractor trackers are the most uncontrolled data stores in manufacturing.',
    },
    blindSpotBanner: 'Factory biometric attendance and contractor databases are high-risk data stores that most manufacturers haven\'t mapped for DPDP.',
    useCaseCard: {
      headline: 'Are your biometric systems compliant?',
      stat: 'Highest-risk data category',
      description: 'Biometric attendance and CCTV are among the strictest categories. Most factories run them without consent.',
    },
    relevance: {
      activities: ['employee_payroll', 'vendor_mgmt'],
      subjects: ['employees', 'vendors', 'visitors'],
      elements: ['contact_info', 'gov_ids', 'biometric'],
      systems: ['hrms', 'onprem', 'sheets'],
    },
  },

  // ─── LOGISTICS ──────────────────────────────────────────
  logistics: {
    label: 'Logistics & Supply Chain',
    shortLabel: 'Logistics',
    commonProcesses: [
      'Driver onboarding & licence verification',
      'Shipment tracking & proof of delivery',
      'Last-mile delivery with customer contact',
      'Fleet management & GPS vehicle tracking',
      'Warehouse worker management & access control',
      'Customer returns & reverse logistics',
    ],
    typicalDataElements: [
      { element: 'Driver licences, Aadhaar, PAN, bank details', category: 'Government IDs' },
      { element: 'Customer delivery addresses & phone numbers', category: 'Identity' },
      { element: 'GPS location data — real-time vehicle tracking', category: 'Location' },
      { element: 'Delivery OTPs, signature captures, photo proofs', category: 'Technical' },
      { element: 'Warehouse worker biometrics & shift attendance', category: 'Biometric' },
    ],
    missedDataPoints: [
      { point: 'GPS tracking data of drivers and delivery personnel', risk: 'high', note: 'Continuous location tracking of individuals is personal data processing' },
      { point: 'Customer phone numbers exposed to delivery partners', risk: 'high', note: 'Customer contact shared during delivery is a data disclosure event' },
      { point: 'Delivery OTP logs retained in telecom/SMS systems', risk: 'medium', note: 'OTP logs tied to phone numbers are personal data with retention obligations' },
      { point: 'Photo proof of delivery showing customer premises', risk: 'medium', note: 'Delivery photos may capture identifiable features of customers\' homes' },
      { point: 'Fuel card and toll data linked to driver profiles', risk: 'low', note: 'Transaction data tied to identifiable drivers is personal data' },
    ],
    typicalSystems: ['Transport Management System (TMS)', 'Fleet/GPS platforms (BlackBuck, Fleetx)', 'Driver mobile apps', 'Customer tracking portals/apps', 'Warehouse Management System (WMS)', 'SMS/OTP aggregators (MSG91, Gupshup)'],
    dpdpNotes: [
      'GPS tracking of drivers is continuous personal data processing — explicit consent and purpose limitation are required.',
      'Customer phone numbers shared with delivery personnel during last-mile delivery need masking or controlled access.',
      'Proof of delivery photos may inadvertently capture personal or sensitive information about customers.',
    ],
    stepHints: {
      activities: 'Every shipment involves customer data (address, phone) shared across your systems, drivers, and partner platforms.',
      subjects: 'Drivers and delivery personnel are data principals too. Their GPS location, licence data, and attendance are all regulated.',
      elements: 'GPS tracking data is personal data. If you track vehicles with drivers, you\'re processing location data of identifiable individuals.',
      systems: 'Fleet management apps, driver mobile apps, and SMS aggregators (for OTPs) all store personal data that\'s rarely mapped.',
    },
    blindSpotBanner: 'Every delivery exposes customer phone numbers and addresses to drivers and aggregator platforms — that\'s DPDP-regulated sharing.',
    useCaseCard: {
      headline: 'Is your GPS tracking DPDP-compliant?',
      stat: 'Continuous location processing',
      description: 'Fleet tracking, delivery OTPs, and customer data sharing happen on every shipment. Most logistics firms haven\'t mapped it.',
    },
    relevance: {
      activities: ['vendor_mgmt', 'employee_payroll', 'payments'],
      subjects: ['customers', 'employees', 'vendors'],
      elements: ['contact_info', 'gov_ids', 'digital'],
      systems: ['saas', 'sheets', 'crm'],
    },
  },

  // ─── REAL ESTATE ────────────────────────────────────────
  real_estate: {
    label: 'Real Estate & Property',
    shortLabel: 'Real Estate',
    commonProcesses: [
      'Property sales — lead capture, site visits, booking',
      'Buyer KYC & RERA registration documentation',
      'Rental management — tenant onboarding & agreements',
      'Society/RWA management — resident data & maintenance',
      'Broker channel management & commission processing',
      'Marketing — digital campaigns, SMS blasts, events',
    ],
    typicalDataElements: [
      { element: 'Buyer/tenant Aadhaar, PAN, address proofs', category: 'Government IDs' },
      { element: 'Bank details for EMI, rent deposits, refunds', category: 'Financial' },
      { element: 'Property visit logs, preferences, budget data', category: 'Behavioral' },
      { element: 'Broker PAN, commission details, bank accounts', category: 'Financial' },
      { element: 'Resident data — family details, vehicle numbers, photos', category: 'Identity' },
    ],
    missedDataPoints: [
      { point: 'Customer leads shared across broker network via WhatsApp', risk: 'high', note: 'Sharing buyer details with brokers is third-party data disclosure' },
      { point: 'CCTV in residential complexes capturing residents and visitors', risk: 'high', note: 'Residential CCTV processes personal data of residents daily' },
      { point: 'Visitor logs at society gates with photo ID records', risk: 'medium', note: 'Gate registers with ID copies store personal data without clear retention' },
      { point: 'RERA registration documents containing extensive personal details', risk: 'medium', note: 'RERA filings contain personal data with long-term retention needs' },
      { point: 'WhatsApp groups for lead management and broker coordination', risk: 'medium', note: 'Uncontrolled WhatsApp sharing of buyer/tenant details is data processing' },
    ],
    typicalSystems: ['CRM (Sell.do, LeadSquared, NoBroker)', 'Property management platforms', 'WhatsApp (primary lead management tool)', 'Society management apps (MyGate, NoBroker)', 'Excel/Sheets for broker tracking', 'Payment gateways for booking/rent'],
    dpdpNotes: [
      'Real estate has one of the highest rates of informal data sharing — leads passed between builders, brokers, and channel partners on WhatsApp.',
      'Residential societies managing resident data (family details, vehicle, domestic help) are data fiduciaries under DPDP.',
      'RERA documentation requirements overlap with DPDP — data collected for registration may need separate consent for other uses.',
    ],
    stepHints: {
      activities: 'Property leads generate rich personal profiles — budget, location preference, family size, employer — often shared freely across broker networks.',
      subjects: 'Buyers are the obvious ones, but tenants, society residents, visitors logged at gates, and even domestic help data in society records are data principals.',
      elements: 'KYC for property transactions involves Aadhaar, PAN, bank details, and employer references — among the most comprehensive data collection.',
      systems: 'WhatsApp is the #1 CRM in Indian real estate. Lead data, buyer documents, and broker coordination all flow through it uncontrolled.',
    },
    blindSpotBanner: 'WhatsApp is the de facto CRM in Indian real estate — buyer leads and KYC documents flow through it daily without any DPDP controls.',
    useCaseCard: {
      headline: 'Is WhatsApp your unofficial CRM?',
      stat: '#1 uncontrolled data channel',
      description: 'Property leads, KYC documents, and broker coordination happen on WhatsApp. None of it is DPDP-mapped.',
    },
    relevance: {
      activities: ['customer_kyc', 'marketing', 'payments'],
      subjects: ['customers', 'employees', 'vendors'],
      elements: ['contact_info', 'gov_ids', 'financial'],
      systems: ['crm', 'sheets', 'saas'],
    },
  },

  // ─── PROFESSIONAL SERVICES ──────────────────────────────
  professional_services: {
    label: 'Professional Services',
    shortLabel: 'Prof. Services',
    commonProcesses: [
      'Client engagement & matter/project management',
      'Employee & consultant hiring, onboarding',
      'Staffing & manpower deployment to client sites',
      'Billing, invoicing & expense management',
      'Document management & knowledge repositories',
      'Compliance audits & regulatory filings on behalf of clients',
    ],
    typicalDataElements: [
      { element: 'Client contact details, business information', category: 'Identity' },
      { element: 'Employee PAN, Aadhaar, bank accounts for payroll', category: 'Government IDs' },
      { element: 'Deployed staff data shared with client organisations', category: 'Employment' },
      { element: 'Billing records with client personnel details', category: 'Financial' },
      { element: 'Client documents containing their customers\' personal data', category: 'Identity' },
    ],
    missedDataPoints: [
      { point: 'Client personal data embedded in project files and emails', risk: 'high', note: 'Consulting firms often handle client customer data without recognizing processor obligations' },
      { point: 'Deployed staff data shared with client HR and admin teams', risk: 'high', note: 'Staffing firms share employee data with clients — a processor relationship under DPDP' },
      { point: 'Old engagement files with client data retained on shared drives', risk: 'medium', note: 'Project archives often contain personal data far past its purpose' },
      { point: 'Conference and event attendee data from business development', risk: 'medium', note: 'Event registrations capture personal data needing consent and retention limits' },
      { point: 'Consultant laptop data when working across multiple clients', risk: 'medium', note: 'Local copies of client data on consultant devices are processing events' },
    ],
    typicalSystems: ['Practice management / CRM', 'Document management (SharePoint, Google Drive)', 'HRMS / payroll systems', 'Email (primary communication)', 'Time tracking & billing platforms', 'Shared drives (Google Drive, OneDrive, Dropbox)'],
    dpdpNotes: [
      'Professional services firms are often both data fiduciaries (their own employees/clients) and data processors (handling client data).',
      'Staffing companies face unique challenges — deployed staff data is shared with and processed by client organisations.',
      'Law firms and CAs handle client personal data within privileged communications — DPDP creates new obligations beyond professional privilege.',
    ],
    stepHints: {
      activities: 'Client KYC, staffing deployments, and compliance audits all involve processing personal data — both yours and your clients\' stakeholders.',
      subjects: 'Beyond employees, think about deployed staff, client contacts, event attendees, and job applicants across multiple open positions.',
      elements: 'Client engagement files often contain third-party personal data — your client\'s customers, employees, or vendors. That\'s your processing too.',
      systems: 'Email, shared drives, and project folders are the biggest repositories of untracked personal data in professional services.',
    },
    blindSpotBanner: 'Consulting and staffing firms are both fiduciaries AND processors — handling their own data plus clients\' personal data simultaneously.',
    useCaseCard: {
      headline: 'Are you a fiduciary, processor, or both?',
      stat: 'Dual DPDP obligations',
      description: 'Professional firms handle their own data plus clients\'. Most haven\'t mapped where one obligation ends and the other begins.',
    },
    relevance: {
      activities: ['customer_kyc', 'employee_payroll', 'vendor_mgmt'],
      subjects: ['customers', 'employees', 'applicants'],
      elements: ['contact_info', 'gov_ids', 'financial'],
      systems: ['saas', 'sheets', 'crm'],
    },
  },
};

// ──────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ──────────────────────────────────────────────────────────

export function getIndustryRelevance(industryId, stepKey) {
  return INDUSTRY_INTELLIGENCE[industryId]?.relevance?.[stepKey] || [];
}

export function getStepHint(industryId, stepKey) {
  return INDUSTRY_INTELLIGENCE[industryId]?.stepHints?.[stepKey] || null;
}

export function getMissedDataPoints(industryId) {
  return INDUSTRY_INTELLIGENCE[industryId]?.missedDataPoints || [];
}

export function getIndustryLabel(industryId) {
  return INDUSTRY_INTELLIGENCE[industryId]?.shortLabel || '';
}

export function getAllIndustryCards() {
  return Object.entries(INDUSTRY_INTELLIGENCE).map(([id, data]) => ({
    id,
    label: data.label,
    shortLabel: data.shortLabel,
    ...data.useCaseCard,
    blindSpot: data.blindSpotBanner,
  }));
}
